import React, { useState, useEffect, useCallback } from "react";
import { FormField, hasError, IFieldProps, onChangeField } from "~/components/Form";
import { Intent } from "~/lib/intent";
import { ID } from "~/lib/model";
import { fetchAgencyDomains } from "../api";
import { GroupedSelectInput, ISelectGroup } from "~/components/Controls/Select/GroupedSelectInput";
import { debounce } from "lodash";

type ISelectDomainProps = {
  agencyId: number;
  disabled?: boolean;
  placeholder?: string;
  value?: number | null;
  onChange?: (value: number | null) => void;
  domainName?: string;
  ownDomainsOnly?: boolean;
};

export const SelectDomain = ({ agencyId, disabled, placeholder, value, onChange, domainName, ownDomainsOnly }: ISelectDomainProps) => {
  const [groups, setGroups] = useState<ISelectGroup<number>[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasLoadedForValue, setHasLoadedForValue] = useState<number | null>(null);
  const [selectedDomain, setSelectedDomain] = useState<{value: number, label: string} | null>(null);

  const loadDomains = async (prefix?: string, loadAll: boolean = false) => {
    if (!agencyId) return [];
    setLoading(true);
    try {
      const res = await fetchAgencyDomains(agencyId, loadAll ? prefix : undefined);

      const agencyItems = (res.agencyDomains || []).map((d) => ({
        label: d.domain,
        value: d.id as number,
      }));

      const otherItems = loadAll
        ? (res.otherDomains || []).map((d) => ({
            label: d.domain,
            value: d.id as number,
          }))
        : (() => {
            const existingOtherItems = groups.find(g => g.label === "Other domains")?.items || [];
            if (selectedDomain && !agencyItems.some(item => item.value === selectedDomain.value)) {
              const alreadyExists = existingOtherItems.some(item => item.value === selectedDomain.value);
              if (!alreadyExists) {
                return [...existingOtherItems, selectedDomain];
              }
            }
            return existingOtherItems;
          })();

      const newGroups = [
        {
          label: "Agency domains",
          items: agencyItems,
        },
        // p2p agencies (with a DSP connection type) are limited to their own ad system domains.
        ...(ownDomainsOnly ? [] : [{ label: "Other domains", items: otherItems }]),
      ];

      setGroups(newGroups);
      return newGroups;
    } catch (error) {
      console.error("Failed to load domains:", error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const debouncedLoadDomains = useCallback(
    debounce((query: string) => {
      loadDomains(query || undefined, true);
    }, 300),
    [agencyId]
  );

  useEffect(() => {
    const loadInitialData = async () => {
      if (value && domainName && hasLoadedForValue !== value) {
        const initialGroups = await loadDomains(undefined, false);

        const allItems = initialGroups.flatMap(g => g.items);
        const hasValue = allItems.some(item => item.value === value);

        if (!hasValue) {
          await loadDomains(domainName, true);
        }
        setHasLoadedForValue(value);
      } else if (value && hasLoadedForValue !== value) {
        await loadDomains(undefined, false);
        setHasLoadedForValue(value);
      } else if (!value) {
        await loadDomains(undefined, false);
        setHasLoadedForValue(null);
      }
    };

    loadInitialData();
  }, [agencyId, value, domainName]);

  return (
    <GroupedSelectInput<number>
      value={value ?? null}
      onChange={(val) => {
        if (val) {
          const allItems = groups.flatMap(g => g.items);
          const domain = allItems.find(item => item.value === val);
          if (domain) {
            setSelectedDomain(domain);
          }
        } else {
          setSelectedDomain(null);
        }

        onChange?.(val ?? null);
      }}
      groups={groups}
      disabled={disabled}
      placeholder={placeholder || "Select domain"}
      searchable
      clearable
      rightIcon={loading ? <i className="fa fa-spinner fa-spin" /> : undefined}
      onQueryChange={debouncedLoadDomains}
    />
  );
};


export const SelectDomainField = (props: IFieldProps<ISelectDomainProps>) => (
  <FormField {...props}>
    <SelectDomain
      {...props.options}
      intent={hasError(props) && Intent.DANGER}
      value={props.field.value}
      onChange={(value) => onChangeField(props, value ?? null)}
    />
  </FormField>
);
